import { cn } from '@/lib/utils'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import type { ComponentType, MouseEventHandler } from 'react'

export type SortDirection = 'asc' | 'desc'

export type DirectionalSortConfig = {
  asc: string
  desc: string
  ascAliases?: string[]
  descAliases?: string[]
  defaultDirection?: SortDirection
  defaultSort?: string
}

export type DirectionalSortState = {
  active: boolean
  direction: SortDirection | null
  nextSort: string
}

export function getDirectionalSortState(
  sort: string | null | undefined,
  config: DirectionalSortConfig
): DirectionalSortState {
  // Widened so the (possibly missing) current sort can be looked up directly.
  const ascValues: Array<string | null | undefined> = [
    config.asc,
    ...(config.ascAliases ?? []),
  ].filter(Boolean)
  const descValues: Array<string | null | undefined> = [
    config.desc,
    ...(config.descAliases ?? []),
  ].filter(Boolean)
  const isAscending = ascValues.includes(sort)
  const isDescending = descValues.includes(sort)
  const direction: SortDirection | null = isAscending ? 'asc' : isDescending ? 'desc' : null
  const defaultDirection = config.defaultDirection ?? 'asc'

  return {
    active: Boolean(direction),
    direction,
    nextSort:
      direction === 'asc'
        ? config.desc
        : direction === 'desc'
          ? config.asc
          : (config.defaultSort ?? config[defaultDirection]),
  }
}

export type SortableColumnHeaderProps = {
  /** Plain-text label; it is also used to build the sort button's accessible name. */
  children: string
  icon?: ComponentType<{ className?: string }>
  active?: boolean
  direction?: SortDirection | null
  className?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  wrap?: boolean
}

export function SortableColumnHeader({
  children,
  icon: Icon,
  active = false,
  direction = null,
  className = '',
  onClick,
  wrap = false,
}: SortableColumnHeaderProps) {
  const DirectionIcon =
    direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ArrowUpDown
  const sortDirectionLabel =
    direction === 'asc'
      ? 'sorted ascending'
      : direction === 'desc'
        ? 'sorted descending'
        : 'sort column'
  const content = (
    <>
      {Icon ? <Icon className="size-3.5 shrink-0" /> : null}
      <span className={wrap ? 'whitespace-normal' : 'whitespace-nowrap'}>{children}</span>
      {onClick ? (
        <DirectionIcon
          aria-hidden="true"
          className={cn('size-3.5 shrink-0', active ? 'opacity-100' : 'opacity-55')}
        />
      ) : null}
    </>
  )
  const sharedClassName = cn(
    'flex w-full min-w-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground transition-colors',
    onClick && 'cursor-pointer hover:text-foreground',
    active && 'text-foreground',
    className
  )

  if (!onClick) {
    return <div className={sharedClassName}>{content}</div>
  }

  return (
    <button
      type="button"
      className={sharedClassName}
      aria-label={`${children}, ${sortDirectionLabel}`}
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
    >
      {content}
    </button>
  )
}
